import { engineComponent, ArrayUtils } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";
import { Tweens } from "../Components/Effects/Tweens";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { SwitchElement, SwitchElementAttributes } from "../Components/Elements/Graphics/SwitchElement";

@engineComponent(ComponentTypes.DisplayObject)
export class AnimatedElement extends SwitchElement {
    protected originY: number = 0;
    protected originX: number = 0;
    protected _timer: FrameTimerEventEmitter;
    protected _positionOffset: number = 3;
    protected _rotationOffset: number = 3;
    protected _scaleOffset: number = 0.02;
    protected _rotationDuration: number = 240;
    protected _scaleDuration: number = 240;
    protected _timerRange: Array<number> = [120, 240];
    protected _positionRangeDuration: Array<number> = [360, 540];

    constructor() {
        super();
        this._timer = CreateFrameTimer("timer");
        this._timer.on(FrameTimerEvents.Complete, this.move.bind(this));
    }

    public createByNode(node: Element) {
        super.createByNode(node);
        this.originY = this.position.y;
        this.originX = this.position.x;

        if (node.hasAttribute("PositionOffset")) {
            this._positionOffset = parseInt(node.getAttribute("PositionOffset"));
        }
        if (node.hasAttribute("RotationOffset")) {
            this._rotationOffset = parseInt(node.getAttribute("RotationOffset"));
        }
        if (node.hasAttribute("ScaleOffset")) {
            this._scaleOffset = parseFloat(node.getAttribute("ScaleOffset"));
        }
        if (node.hasAttribute("RotationDuration")) {
            this._rotationDuration = parseInt(node.getAttribute("RotationDuration"));
        }
        if (node.hasAttribute("ScaleDuration")) {
            this._scaleDuration = parseInt(node.getAttribute("ScaleDuration"));
        }
        if (node.hasAttribute("TimerRange")) {
            let timerRange: Array<number> = ArrayUtils.numberArray(node.getAttribute("TimerRange"));
            if (timerRange != null && timerRange.length === 2) {
                this._timerRange = timerRange;
            } else {
                if (DEBUG) {
                    this.log("WARNING timer range need to has 2 parameters");
                }
            }
        }
        if (node.hasAttribute("PositionRangeDuration")) {
            let positionRangeDuration: Array<number> = ArrayUtils.numberArray(
                node.getAttribute("PositionRangeDuration"),
            );
            if (positionRangeDuration != null && positionRangeDuration.length === 2) {
                this._positionRangeDuration = positionRangeDuration;
            } else {
                if (DEBUG) {
                    this.log("WARNING position range duration need to has 2 parameters");
                }
            }
        }

        let container: PIXI.Container = new PIXI.Container();
        if (this.children != null) {
            while (this.children.length !== 0) {
                container.addChild(this.children[0]);
            }
        }
        this.addChild(container);

        if (node.hasAttribute("PivotX")) {
            this.pivot.x = parseInt(node.getAttribute("PivotX"));
        } else {
            this.pivot.x = this.width / 2;
        }

        if (node.hasAttribute("PivotY")) {
            this.pivot.y = parseInt(node.getAttribute("PivotY"));
        } else {
            this.pivot.y = this.height / 2;
        }

        if (node.hasAttribute("Start")) {
            this.start();
        }
    }

    public updateByNode(node: Element) {
        super.updateByNode(node);

        if (node.hasAttribute(SwitchElementAttributes.CASE)) {
            this.goToCase(node.getAttribute(SwitchElementAttributes.CASE));
        }

        if (node.hasAttribute("OriginX")) {
            this.originX = parseInt(node.getAttribute("OriginX"));
        }

        if (node.hasAttribute("OriginY")) {
            this.originY = parseInt(node.getAttribute("OriginY"));
        }

        if (node.hasAttribute("PivotX")) {
            this.pivot.x = parseInt(node.getAttribute("PivotX"));
        }

        if (node.hasAttribute("PivotY")) {
            this.pivot.y = parseInt(node.getAttribute("PivotY"));
        }

        if (node.hasAttribute("Start")) {
            this.start();
        }
    }

    public start(): void {
        // this.pivot.x = this.width / 2;
        // this.pivot.y = this.height / 2;
        this.stop();
        // this.animate();

        this.move();
    }

    public stop(): void {
        this._timer.stop();
        Tweens.removeTweens(this, "all");
        if (this.children != null && this.children.length > 0) {
            for (let i: number = 0; i < this.children.length; i++) {
                Tweens.removeTweens(this.children[i] as ContainerElement, "all");
                for (let j: number = 0; j < (this.children[i] as ContainerElement).children.length; j++) {
                    Tweens.removeTweens((this.children[i] as ContainerElement).children[j] as ContainerElement, "all");
                }
            }
        }
    }

    public clone(): AnimatedElement {
        const element: AnimatedElement = new AnimatedElement();
        element.style = this.style;
        element.position.x = this.position.x;
        element.position.y = this.position.y;
        element.originX = this.originX;
        element.originY = this.originY;
        element.scale.x = this.scale.x;
        element.scale.y = this.scale.y;
        element.visible = this.visible;
        element.removeChildren();
        element.elements = this.elements.clone();
        element.addChild(element.elements);

        element._positionOffset = this._positionOffset;
        element._rotationOffset = this._rotationOffset;
        element._scaleOffset = this._scaleOffset;
        element._rotationDuration = this._rotationDuration;
        element._scaleDuration = this._scaleDuration;
        element._timerRange = this._timerRange;
        element._positionRangeDuration = this._positionRangeDuration;
        element.case = this.case;
        element.start();
        element.markAsCloned();
        return element;
    }

    public animate(): void {
        let randomRotation: number = Math.round(this.getRandom(-this._rotationOffset - 1, this._rotationOffset + 1));
        if (randomRotation === -1) randomRotation = 1;

        Tweens.removeTweens(this, "rotation");
        if (this.rotation !== 0) {
            Tweens.rotationTween(this, {
                degrees: [this.radians2degrees(this.rotation), 0],
                tweenDuration: 5,
                callbackFunction: (): void => {
                    Tweens.rotationTween(this, {
                        degrees: [this.radians2degrees(this.rotation), randomRotation + 1, 0, -randomRotation - 1, 0],
                        tweenDuration: this._rotationDuration,
                        tweenLoop: true,
                    });
                },
            });
        } else {
            Tweens.rotationTween(this, {
                degrees: [0, randomRotation + 1, 0, -randomRotation - 1, 0],
                tweenDuration: this._rotationDuration,
                tweenLoop: true,
            });
        }

        Tweens.removeTweens(this, "scale");
        if (this.scale.x !== 1 && this.scale.y !== 1) {
            Tweens.scaleTween(this, {
                tweenValues: [new PIXI.Point(this.scale.x, this.scale.y), new PIXI.Point(1, 1)],
                tweenDuration: 5,
                callbackFunction: (): void => {
                    Tweens.scaleTween(this, {
                        tweenValues: [
                            new PIXI.Point(1, 1),
                            new PIXI.Point(1 - this._scaleOffset, 1 + this._scaleOffset),
                            new PIXI.Point(1, 1),
                        ],
                        tweenDuration: this._scaleDuration,
                        tweenLoop: true,
                    });
                },
            });
        } else {
            Tweens.scaleTween(this, {
                tweenValues: [
                    new PIXI.Point(1, 1),
                    new PIXI.Point(1 - this._scaleOffset, 1 + this._scaleOffset),
                    new PIXI.Point(1, 1),
                ],
                tweenDuration: this._scaleDuration,
                tweenLoop: true,
            });
        }
    }

    public move(): void {
        Tweens.removeTweens(this, "position");
        if (this.worldVisible) {
            let randomDuration: number = Math.round(
                this.getRandom(
                    this._positionRangeDuration[0],
                    this._positionRangeDuration[this._positionRangeDuration.length - 1],
                ),
            );
            let randomTimer: number = Math.round(
                this.getRandom(this._timerRange[0], this._timerRange[this._timerRange.length - 1]),
            );
            let randomX: number = Math.round(this.getRandom(-this._positionOffset, this._positionOffset));
            let randomY: number = Math.round(this.getRandom(-this._positionOffset, this._positionOffset));
            let childX: number = this.originX;
            let childY: number = this.originY;

            if (childX + randomX > this.originX) {
                if (childX + randomX > this.originX + this._positionOffset) {
                    randomX = this._positionOffset;
                }
            } else {
                if (childX + randomX < this.originX - this._positionOffset) {
                    randomX = -this._positionOffset;
                }
            }
            if (childY + randomY > this.originY) {
                if (childY + randomY > this.originY + this._positionOffset) {
                    randomY = this._positionOffset;
                }
            } else {
                if (childY + randomY < this.originY - this._positionOffset) {
                    randomY = -this._positionOffset;
                }
            }

            Tweens.removeTweens(this, "position");
            Tweens.positionTween(this, {
                tweenX: [this.position.x, this.originX + randomX],
                tweenY: [this.position.y, this.originY + randomY],
                tweenDuration: randomDuration,
                tweenEase: "power1InOut",
                callbackFunction: this.move,
                callbackScope: this,
            });
        }
    }

    public getRandom(min: number, max: number): number {
        return Math.random() * (max - min) + min;
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/AnimatedElement.ts
 **/